/**
 * @file api-chat.ts
 * @description API-клиент окна «Сообщения» кабинета магазина (phase10.7;
 * phase19.31 — roles.md R4, SC48, SC54, SC62, SC64; контракт
 * cabinet-api-contract.md §12).
 *
 * Типы зеркалят ответы сервера (`apps/api/src/chat/chat-views.ts`,
 * `staff-inbox.service.ts`). Ответы `{ data }` разворачивает `apiFetch`,
 * ошибки `{ error: { code, message, details } }` приходят как `ApiError` —
 * их текст для человека подбирает `components/chat/chat-errors.ts`.
 *
 * Меню и полосы диалога строятся по `actions` карточки, а не по ролям:
 * правила «кто что может» живут на сервере.
 */

import { apiFetch } from "./api-client";

export type ChatAuthorType = "CUSTOMER" | "STAFF" | "SYSTEM";

/** Вкладка списка диалогов. */
export type ChatTab = "all" | "mine" | "archive";

/** Сотрудник: id и имя (displayName, иначе email). */
export interface ChatStaffPerson {
  id: string;
  name: string;
  displayName: string | null;
}

/** Сотрудник в служебной записи: id null — сотрудник удалён, имя — снимком. */
export interface ChatEventPerson {
  id: string | null;
  name: string;
}

/** Вид служебной записи диалога (R4.15). */
export type ConversationEventType =
  | "TAKEN"
  | "TRANSFERRED"
  | "RETURNED_TO_QUEUE"
  | "ARCHIVED"
  | "UNARCHIVED"
  | "ASSIGNEE_RELEASED";

/** Причина автоматического «в архив» (R4.17): заказ завершён / давно без событий. */
export type ConversationArchiveReason = "order_completed" | "inactive";

/**
 * Служебная запись: `actor: null` — сделала система. Роли участников —
 * словами по каталогу ролей на момент ответа; null — система, сотрудник
 * удалён или больше не работает в магазине.
 */
export interface ConversationEventDto {
  id: string;
  type: ConversationEventType;
  at: string;
  actor: ChatEventPerson | null;
  from: ChatEventPerson | null;
  to: ChatEventPerson | null;
  actorRole?: string | null;
  fromRole?: string | null;
  toRole?: string | null;
  /** Только у автоматического `ARCHIVED`; иначе null. */
  reason?: ConversationArchiveReason | null;
}

/** Сообщение для сотрудника (у реплики сотрудника — имя автора). */
export interface ChatMessageDto {
  id: string;
  conversationId: string;
  authorType: ChatAuthorType;
  authorUserId: string | null;
  author: { id: string; name: string } | null;
  /** Моя реплика — справа, цветом действия. */
  isMine: boolean;
  body: string;
  isMarketing: boolean;
  readByCustomerAt: string | null;
  readByStaffAt: string | null;
  createdAt: string;
}

/** Заказ диалога: стадия, сумма и кто ведёт заказ (R4.10). */
export interface ConversationOrderContext {
  id: string;
  number: string;
  stageName: string;
  stageKind: string | null;
  stageColor: string | null;
  /** Деньги строкой с двумя знаками: "1350.00". */
  totalGross: string;
  currency: string;
  owner: ChatStaffPerson | null;
}

/** Последнее сообщение для строки списка. */
export interface LastMessageView {
  at: string;
  authorType: ChatAuthorType;
  author: { id: string; name: string } | null;
  isMine: boolean;
  preview: string;
}

/** Признаки строки для текущего сотрудника. */
export interface ConversationFlags {
  transferredToMe: boolean;
  unarchivedUnseen: boolean;
  unassigned: boolean;
  assignedToMe: boolean;
}

/** Строка списка диалогов. */
export interface StaffConversation {
  id: string;
  shopId: string;
  customerId: string;
  customer: { id: string; displayName: string | null; email: string | null };
  contextOrderId: string | null;
  contextDealId: string | null;
  contextOrder: ConversationOrderContext | null;
  contextDeal: { id: string; displayName: string } | null;
  assignedUserId: string | null;
  assignedUser: ChatStaffPerson | null;
  archivedAt: string | null;
  lastMessageAt: string | null;
  /** Время последнего события диалога — по нему порядок списка (R4.18). */
  lastActivityAt: string;
  /** Мои непрочитанные сообщения клиента (личные, R4.19). */
  unreadCount: number;
  /** Прежнее имя того же числа — читать `unreadCount`. */
  unreadByStaff: number;
  unreadByCustomer: number;
  createdAt: string;
  updatedAt: string;
  lastMessagePreview: string | null;
  lastMessageFromStaff: boolean | null;
  lastMessage: LastMessageView | null;
  lastEvent: ConversationEventDto | null;
  flags: ConversationFlags;
}

/** Что сотрудник может сделать с диалогом прямо сейчас. */
export interface ConversationActions {
  send: boolean;
  markRead: boolean;
  take: boolean;
  transfer: boolean;
  returnToQueue: boolean;
  archive: boolean;
  unarchive: boolean;
}

/** Карточка диалога: строка списка + шапка и панель сведений. */
export interface ConversationDetail extends StaffConversation {
  customer: StaffConversation["customer"] & { phone: string | null };
  marketingConsent: { granted: boolean; at: string | null };
  actions: ConversationActions;
  /** Вход под сотрудником — только чтение. */
  readOnly: boolean;
}

/** Число диалогов с моим непрочитанным по вкладкам (R4.19; в архиве не бывает). */
export interface ChatUnreadCounts {
  all: number;
  mine: number;
  unassigned: number;
}

/** Числа на вкладках и отборе. */
export interface ChatTabCounts {
  all: number;
  mine: number;
  archive: number;
  unassigned: number;
  unread: ChatUnreadCounts;
}

export interface ConversationListPage {
  items: StaffConversation[];
  nextCursor: string | null;
  counts: ChatTabCounts;
}

export interface ConversationListParams {
  tab: ChatTab;
  unassigned?: boolean;
  customerId?: string;
  /** Диалог конкретного заказа — поиск без перебора списка. */
  contextOrderId?: string;
  search?: string;
  /** `nextCursor` прошлой страницы (`<ISO>|<id>`), как отдал сервер. */
  cursor?: string;
  limit?: number;
}

/** Элемент ленты: сообщение или служебная запись, по времени. */
export type ChatFeedItem =
  | { kind: "message"; at: string; message: ChatMessageDto }
  | { kind: "event"; at: string; event: ConversationEventDto };

/** Страница ленты: последние `limit` сообщений и записи того же отрезка. */
export interface ChatFeedPage {
  items: ChatFeedItem[];
  /** Есть более ранние записи. */
  hasMore: boolean;
  /** `before` для «Показать ранние сообщения»; null — начало переписки. */
  nextCursor: string | null;
}

export interface ChatFeedParams {
  before?: string;
  limit?: number;
}

export interface ChatPollResult {
  messages: ChatMessageDto[];
  events: ConversationEventDto[];
  serverNow: string;
}

/** Кому можно передать диалог. */
export interface TransferCandidate {
  id: string;
  name: string;
  roleCode: string;
  roleName: string;
  openConversations: number;
  leadsOrder: boolean;
  isCurrentAssignee: boolean;
}

export type ChatSummaryKind = "MESSAGE" | "TRANSFERRED_TO_ME" | "UNARCHIVED";

export interface ChatSummary {
  unreadConversations: number;
  unreadMessages: number;
  latest: {
    kind: ChatSummaryKind;
    conversationId: string;
    at: string;
    orderNumber: string | null;
    actorName: string | null;
  } | null;
}

/** Тело действия: каким кабинет видел ответственного (защита от гонки). */
export interface ActionGuard {
  expectedAssignedUserId?: string | null;
}

/** Детали 409 `chat_conversation_state_changed`. */
export interface ConversationStateDetails {
  assignedUser: { id: string; name: string } | null;
  archived: boolean;
}

const BASE = "/admin/chat/conversations";

function listQuery(params: ConversationListParams): string {
  const q = new URLSearchParams({ tab: params.tab });
  if (params.unassigned) q.set("unassigned", "true");
  if (params.customerId) q.set("customerId", params.customerId);
  if (params.contextOrderId) q.set("contextOrderId", params.contextOrderId);
  if (params.search) q.set("search", params.search);
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  return q.toString();
}

function feedQuery(params: ChatFeedParams): string {
  const q = new URLSearchParams();
  if (params.before) q.set("before", params.before);
  if (params.limit) q.set("limit", String(params.limit));
  const text = q.toString();
  return text ? `?${text}` : "";
}

/** Запрос с возможностью прервать (смена магазина, смена диалога). */
type Signal = AbortSignal | undefined;

export const adminChat = {
  list: (params: ConversationListParams, signal?: Signal) =>
    apiFetch<ConversationListPage>(`${BASE}?${listQuery(params)}`, { signal }),

  summary: (signal?: Signal) => apiFetch<ChatSummary>("/admin/chat/summary", { signal }),

  findOne: (id: string, signal?: Signal) => apiFetch<ConversationDetail>(`${BASE}/${id}`, { signal }),

  /** Лента: без `before` — последние сообщения; с `before` — более ранняя страница. */
  feed: (id: string, params: ChatFeedParams = {}, signal?: Signal) =>
    apiFetch<ChatFeedPage>(`${BASE}/${id}/feed${feedQuery(params)}`, { signal }),

  poll: (id: string, since: string, signal?: Signal) =>
    apiFetch<ChatPollResult>(`${BASE}/${id}/poll?since=${encodeURIComponent(since)}`, { signal }),

  ensure: (input: { customerId: string; contextOrderId?: string; contextDealId?: string }) =>
    apiFetch<ConversationDetail>(BASE, { method: "POST", json: input }),

  send: (id: string, body: string, isMarketing: boolean) =>
    apiFetch<ChatMessageDto>(`${BASE}/${id}/messages`, { method: "POST", json: { body, isMarketing } }),

  markRead: (id: string) => apiFetch<{ ok: boolean }>(`${BASE}/${id}/mark-read`, { method: "POST" }),

  staff: (conversationId: string) =>
    apiFetch<TransferCandidate[]>(`/admin/chat/staff?conversationId=${encodeURIComponent(conversationId)}`),

  take: (id: string, guard: ActionGuard) =>
    apiFetch<ConversationDetail>(`${BASE}/${id}/take`, { method: "POST", json: guard }),

  transfer: (id: string, toUserId: string, guard: ActionGuard) =>
    apiFetch<ConversationDetail>(`${BASE}/${id}/transfer`, { method: "POST", json: { toUserId, ...guard } }),

  /** Логист возвращает диалог — ответ без карточки: он его больше не видит. */
  returnToQueue: (id: string) => apiFetch<{ ok: true }>(`${BASE}/${id}/return-to-queue`, { method: "POST" }),

  archive: (id: string, guard: ActionGuard) =>
    apiFetch<ConversationDetail>(`${BASE}/${id}/archive`, { method: "POST", json: guard }),

  unarchive: (id: string) => apiFetch<ConversationDetail>(`${BASE}/${id}/unarchive`, { method: "POST" }),
};
